import { useState, useCallback, useRef } from 'react';
import { CalendarData } from '../types/calendar';

const isNoKnowledgeHitMessage = (message?: string): boolean => {
  const normalized = (message || '').toLowerCase();
  return (
    normalized.includes("don't have any relevant information for that query") ||
    normalized.includes("no relevant information was found in the knowledge base")
  );
};

interface StreamingEvent {
  type: 'metadata' | 'content' | 'complete' | 'error' | 'reasoning';
  chat_id?: string;
  content?: string;
  message?: string;
  sources?: Array<{
    document: string;
    similarity: number;
    chunk_id: string;
  }>;
  showCalendar?: boolean;
  calendarData?: CalendarData;
  performance?: {
    embedding_time?: number;
    database_time?: number;
    ai_time?: number;
    total_time?: number;
    api_calls?: number;
  };
  debugReasoning?: boolean;
  reasoningSummary?: string;
  summary?: string;  // Alternative field name for reasoning summary
}

interface StreamingResponse {
  message: string;
  isStreaming: boolean;
  sources: Array<{
    document: string;
    similarity: number;
    chunk_id: string;
  }>;
  showCalendar: boolean;
  calendarData?: CalendarData;
  performance?: {
    embedding_time?: number;
    database_time?: number;
    ai_time?: number;
    total_time?: number;
    api_calls?: number;
  };
  chatId?: string;
  error?: string;
  debugReasoning?: boolean;
  reasoningSummary?: string;
}

export const useStreamingQuery = () => {
  const [response, setResponse] = useState<StreamingResponse>({
    message: '',
    isStreaming: false,
    sources: [],
    showCalendar: false,
    calendarData: undefined,
  });
  
  const messageUpdateCallbackRef = useRef<((message: string, showCalendar: boolean) => void) | null>(null);
  const streamingStartTimeRef = useRef<number | null>(null);
  const firstContentTimeRef = useRef<number | null>(null);
  const lastContentTimeRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendStreamingQuery = useCallback(async (
    apiUrl: string,
    message: string,
    chatId?: string,
    conversationHistory?: Array<{role: string, content: string}>,
    workspaceSlug?: string,
    agentSlug?: string,
    avatarProfileSlug?: string,
  ) => {
    // Abort any existing streaming request
    if (abortControllerRef.current) {
      console.log('🛑 Aborting previous streaming request');
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    // Reset state and timing
    streamingStartTimeRef.current = Date.now();
    firstContentTimeRef.current = null;
    lastContentTimeRef.current = null;
    
    setResponse({
      message: '',
      isStreaming: true,
      sources: [],
      showCalendar: false,
      calendarData: undefined,
      chatId,
    });

    try {
      console.log(`🚀 Starting streaming request to: ${apiUrl} at ${new Date(streamingStartTimeRef.current).toISOString()}`);
      console.log(`📝 Message: ${message}`);
      console.log(`📋 DEBUG: Sending conversation_history:`, JSON.stringify(conversationHistory, null, 2));

      const adminToken = sessionStorage.getItem('admin_token');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        },
        body: JSON.stringify({
          message,
          chat_id: chatId || null,
          conversation_history: conversationHistory || [],
          workspace_slug: workspaceSlug || null,
          agent_slug: agentSlug || null,
          avatar_profile_slug: avatarProfileSlug || null,
          // stream parameter omitted - defaults to true
        }),
        signal // Add abort signal to fetch request
      });

      console.log(`📡 Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Response error: ${errorText}`);
        throw new Error(`API responded with status: ${response.status}`);
      }

      // Check if response is streaming (SSE) or regular JSON
      const contentType = response.headers.get('content-type');
      
      if (contentType?.includes('text/event-stream')) {
        console.log('📺 Processing streaming response (SSE)...');
        await processStreamingResponse(response, signal);
      } else {
        console.log('📄 Processing regular JSON response...');
        await processJsonResponse(response);
      }

    } catch (error) {
      // Don't show error if request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('🛑 Streaming request was aborted');
        setResponse(prev => ({
          ...prev,
          isStreaming: false,
        }));
        return;
      }
      
      console.error('❌ Error in streaming query:', {
        error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        apiUrl,
        requestMessage: message,
        chatId,
        userAgent: navigator.userAgent,
        url: window.location.href
      });
      setResponse(prev => ({
        ...prev,
        isStreaming: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        message: 'Sorry, I encountered an error while processing your request. Please try again.',
      }));
    }
  }, []);

  const processStreamingResponse = async (response: Response, signal: AbortSignal) => {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    let buffer = '';

    try {
      while (true) {
        // Check if the request was aborted
        if (signal.aborted) {
          console.log('🛑 Streaming aborted by user');
          break;
        }
        
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ Streaming completed');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          // Check for abort before processing each line
          if (signal.aborted) {
            console.log('🛑 Streaming aborted during processing');
            return;
          }
          
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            
            if (data === '[DONE]') {
              console.log('🏁 Stream completed with [DONE] marker');
              setResponse(prev => ({ ...prev, isStreaming: false }));
              return;
            }

            try {
              const event: StreamingEvent = JSON.parse(data);
              console.log('📨 Received event:', event.type, event);

              setResponse(prev => {
                console.log(`🎯 setResponse called for event: ${event.type}, prev.message length: ${prev.message.length}`);
                
                switch (event.type) {
                  case 'metadata':
                    const metadataUpdate = {
                      ...prev,
                      sources: event.sources || [],
                      showCalendar: event.showCalendar || false,
                      calendarData: event.calendarData,
                      chatId: event.chat_id,
                      performance: event.performance ? { ...prev.performance, ...event.performance } : prev.performance,
                      debugReasoning: event.debugReasoning || false,
                      reasoningSummary: event.reasoningSummary
                    };
                    console.log(`📊 Metadata update:`, metadataUpdate);
                    return metadataUpdate;

                  case 'reasoning':
                    // Handle reasoning summary event (for non-streaming responses or future streaming support)
                    const reasoningUpdate = {
                      ...prev,
                      reasoningSummary: event.reasoningSummary || event.summary
                    };
                    console.log(`🧠 Reasoning summary received:`, reasoningUpdate.reasoningSummary);
                    return reasoningUpdate;

                  case 'content':
                    if (event.content) {
                      const eventTimestamp = Date.now();
                      
                      // Track first content timing
                      if (!firstContentTimeRef.current) {
                        firstContentTimeRef.current = eventTimestamp;
                      }
                      
                      // Update last content timing
                      lastContentTimeRef.current = eventTimestamp;
                      
                      const newMessage = prev.message + event.content;
                      // console.log(`📝 [${eventTimestamp}] Content update: "${prev.message}" + "${event.content}" = "${newMessage}"`);
                      
                      const contentUpdate = {
                        ...prev,
                        message: newMessage
                      };
                      
                      // Call the callback immediately for real-time updates
                      if (messageUpdateCallbackRef.current) {
                        messageUpdateCallbackRef.current(newMessage, contentUpdate.showCalendar);
                        console.log(`✅ [${eventTimestamp}] Callback updated with accumulated message length: ${newMessage.length}`);
                      } else {
                        console.log(`❌ [${eventTimestamp}] No callback ref set!`);
                      }
                      
                      console.log(`🔄 [${eventTimestamp}] Returning updated response object with message length: ${newMessage.length}`);
                      return contentUpdate;
                    }
                    return prev;

                  case 'complete':
                    const completeTimestamp = Date.now();
                    const totalStreamingTime = streamingStartTimeRef.current ? completeTimestamp - streamingStartTimeRef.current : 0;
                    const contentStreamingTime = firstContentTimeRef.current && lastContentTimeRef.current ? lastContentTimeRef.current - firstContentTimeRef.current : 0;
                    
                    console.log(`⏱️ === STREAMING PERFORMANCE SUMMARY ===`);
                    console.log(`⏱️ Request started: ${streamingStartTimeRef.current ? new Date(streamingStartTimeRef.current).toISOString() : 'unknown'}`);
                    console.log(`⏱️ First content: ${firstContentTimeRef.current ? new Date(firstContentTimeRef.current).toISOString() : 'none'}`);
                    console.log(`⏱️ Last content: ${lastContentTimeRef.current ? new Date(lastContentTimeRef.current).toISOString() : 'none'}`);
                    console.log(`⏱️ Completed: ${new Date(completeTimestamp).toISOString()}`);
                    console.log(`⏱️ Total time: ${totalStreamingTime}ms`);
                    console.log(`⏱️ Content streaming time: ${contentStreamingTime}ms`);
                    console.log(`⏱️ First content delay: ${firstContentTimeRef.current && streamingStartTimeRef.current ? firstContentTimeRef.current - streamingStartTimeRef.current : 0}ms`);
                    console.log(`⏱️ Final message length: ${prev.message.length} characters`);
                    console.log(`⏱️ =====================================`);
                    
                    const completeUpdate = {
                      ...prev,
                      isStreaming: false,
                      performance: event.performance ? { ...prev.performance, ...event.performance } : prev.performance
                    };
                    console.log(`✅ Complete update:`, completeUpdate);
                    return completeUpdate;

                  case 'error':
                    if (isNoKnowledgeHitMessage(event.message)) {
                      const fallbackMessage = event.message || "I don't have any relevant information for that query.";
                      if (messageUpdateCallbackRef.current) {
                        messageUpdateCallbackRef.current(fallbackMessage, prev.showCalendar);
                      }
                      return {
                        ...prev,
                        isStreaming: false,
                        error: undefined,
                        message: fallbackMessage
                      };
                    }
                    const errorUpdate = {
                      ...prev,
                      isStreaming: false,
                      error: event.message || 'Unknown error',
                      message: event.message || 'An error occurred'
                    };
                    console.log(`❌ Error update:`, errorUpdate);
                    return errorUpdate;

                  default:
                    return prev;
                }
              });

            } catch (parseError) {
              console.error('❌ Error parsing event data:', {
                parseError,
                message: parseError instanceof Error ? parseError.message : String(parseError),
                data,
                userAgent: navigator.userAgent,
                url: window.location.href
              });
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  };

  const processJsonResponse = async (response: Response) => {
    const data = await response.json();
    console.log('✅ Received JSON response:', data);

    setResponse(prev => ({
      ...prev,
      isStreaming: false,
      message: data.message || 'No message received',
      sources: data.sources || [],
      showCalendar: data.showCalendar || false,
      calendarData: data.calendarData,
      performance: data.performance,
      chatId: data.chat_id,
      debugReasoning: data.debugReasoning || false,
      reasoningSummary: data.reasoningSummary,
    }));
  };

  const registerMessageUpdateCallback = useCallback((callback: (message: string, showCalendar: boolean) => void) => {
    console.log(`🔗 Setting message update callback - callback exists:`, !!callback);
    messageUpdateCallbackRef.current = callback;
  }, []);

  const clearResponse = useCallback(() => {
    console.log('🧹 Clearing streaming response state for new conversation');
    
    // Abort any ongoing streaming request
    if (abortControllerRef.current) {
      console.log('🛑 Aborting ongoing streaming request during clear');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setResponse({
      message: '',
      isStreaming: false,
      sources: [],
      showCalendar: false,
      calendarData: undefined,
      performance: undefined,
      chatId: undefined,
    });
  }, []);

  return {
    response,
    sendStreamingQuery,
    isStreaming: response.isStreaming,
    registerMessageUpdateCallback,
    clearResponse,
  };
};
